/**
 * Carries PostHog's anonymous distinct id from the shared host
 * (`app.myaltimate.com`, `app.getaltimate.com`), where sign-up and the CLI
 * hand-off start, to the tenant host (`<tenant>.app.myaltimate.com`), where the
 * user is identified after sign-in. PostHog runs with in-memory persistence, so
 * without this the two hosts never share an id and the shared-host visit stays
 * on an anonymous person.
 *
 * The cookie holds only the PostHog-generated anonymous id. It is written on
 * every shared-host page load (the latest load wins) and taken (read and
 * cleared) by the first identify on any host of the same registrable domain.
 */

export const SHARED_HOST_DISTINCT_ID_COOKIE = "ph_did";

const MAX_AGE_SECONDS = 24 * 60 * 60;
const IPV4 = /^\d{1,3}(\.\d{1,3}){3}$/;

/** `foo.app.myaltimate.com` → `myaltimate.com`; null for localhost, IPs and single labels. */
export const registrableDomain = (hostname: string): string | null => {
  const host = hostname.toLowerCase();
  if (IPV4.test(host) || host.includes(":")) return null;
  const labels = host.split(".");
  if (labels.length < 2 || labels.some(label => label === "")) return null;
  if (labels[labels.length - 1] === "localhost") return null;
  return labels.slice(-2).join(".");
};

/** True only for `app.<registrable domain>`, the tenantless shared host. */
export const isSharedAppHost = (hostname: string): boolean => {
  const domain = registrableDomain(hostname);
  return domain !== null && hostname.toLowerCase() === `app.${domain}`;
};

const cookieAttributes = (domain: string, maxAge: number) =>
  `Domain=.${domain}; Path=/; Max-Age=${maxAge}; SameSite=Lax; Secure`;

/**
 * Writes the shared-host anonymous id to a cookie scoped to the registrable
 * domain. No-op unless `hostname` is exactly `app.<registrable domain>`.
 * Returns whether a cookie was written.
 */
export const writeSharedHostDistinctId = (hostname: string, distinctId: string): boolean => {
  if (!distinctId || !isSharedAppHost(hostname)) return false;
  const domain = registrableDomain(hostname) as string;
  document.cookie = `${SHARED_HOST_DISTINCT_ID_COOKIE}=${encodeURIComponent(
    distinctId
  )}; ${cookieAttributes(domain, MAX_AGE_SECONDS)}`;
  return true;
};

const readCookie = (name: string): string | null => {
  for (const part of document.cookie.split(";")) {
    const [key, ...rest] = part.trim().split("=");
    if (key === name) {
      try {
        return decodeURIComponent(rest.join("="));
      } catch {
        return null;
      }
    }
  }
  return null;
};

/**
 * Reads the shared-host anonymous id and clears the cookie (same Domain and
 * Path it was written with). Returns null when there is no usable cookie.
 */
export const takeSharedHostDistinctId = (
  hostname: string = window.location.hostname
): string | null => {
  const domain = registrableDomain(hostname);
  if (domain === null) return null;
  const value = readCookie(SHARED_HOST_DISTINCT_ID_COOKIE);
  if (value === null) return null;
  document.cookie = `${SHARED_HOST_DISTINCT_ID_COOKIE}=; ${cookieAttributes(domain, 0)}`;
  return value || null;
};
